import type { ReactNode } from 'react';
import { PATIENT_PORTAL_IMAGES, type PatientPortalImageKey } from './patientPortalImagery';
import PatientPortalAnnouncementTicker, {
  type PortalAnnouncement,
} from './PatientPortalAnnouncementTicker';

type Props = {
  imageKey: PatientPortalImageKey;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  children?: ReactNode;
  compact?: boolean;
  announcements?: PortalAnnouncement[];
};

export default function PatientPortalPageHero({
  imageKey,
  eyebrow,
  title,
  subtitle,
  children,
  compact,
  announcements,
}: Props) {
  const src = PATIENT_PORTAL_IMAGES[imageKey];

  return (
    <section
      className={`relative flex flex-col overflow-hidden rounded-3xl shadow-xl ring-1 ring-black/5 ${
        compact ? 'min-h-[140px]' : 'min-h-[200px] sm:min-h-[220px]'
      }`}
    >
      <img
        src={src}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-teal-950/90 via-teal-900/75 to-emerald-800/50" />
      <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-amber-400/20 blur-2xl" aria-hidden />
      <div className="absolute -bottom-10 -left-6 h-32 w-32 rounded-full bg-white/10 blur-xl" aria-hidden />

      <div
        className={`relative flex flex-1 flex-col justify-end text-white ${
          compact ? 'px-5 py-6 sm:px-6' : 'px-6 py-8 sm:px-8 sm:py-10'
        }`}
      >
        {eyebrow && (
          <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-teal-100/90 mb-2">
            {eyebrow}
          </p>
        )}
        <h1
          className={`font-semibold tracking-tight text-white drop-shadow-sm ${
            compact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'
          }`}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="mt-2 max-w-lg text-sm sm:text-base text-teal-50/90 leading-relaxed">{subtitle}</p>
        )}
        {children && <div className="mt-4">{children}</div>}
      </div>
      {announcements && announcements.length > 0 && (
        <PatientPortalAnnouncementTicker items={announcements} />
      )}
    </section>
  );
}
